import { Component, input, output, signal } from '@angular/core';
import type { Conversation } from '@afs/chat-core';

/**
 * The caller's open conversations, as tabs, with the ones they only watch
 * folded into a collapsed group underneath. Pure input/output: no service, no
 * HttpClient, no Supabase.
 *
 * These are **this person's own conversations, not a queue.** Spec section 9
 * gives survey the online staff list and its own conversations; a field tablet
 * is never asked to watch the portal's pool.
 *
 * A watched conversation carries **no unread badge and no count of any kind**.
 * Releasing a chat has to actually stop it following you around, or nobody will
 * release anything. The ex-owner stays a participant — a released conversation
 * is `queued` and still has its participants — which is exactly why the badge
 * has to be suppressed here rather than left to fall out of the data.
 *
 * Every class is prefixed `afs-chat-` and lives in this component's own inline
 * styles, so NobleUI's demo `.chat-*` set cannot reach it.
 */
@Component({
  selector: 'app-chat-tabs',
  standalone: true,
  styles: [`
    .afs-chat-tabs { display: flex; gap: .25rem; padding: .4rem .6rem 0; overflow-x: auto; }
    .afs-chat-tab {
      display: inline-flex; align-items: center; gap: .3rem; max-width: 160px;
      border: 1px solid rgba(0,0,0,.14); border-bottom: 0; border-radius: 4px 4px 0 0;
      background: transparent; padding: .25rem .5rem; font-size: .78rem;
      white-space: nowrap; cursor: pointer;
    }
    .afs-chat-tab--active { background: rgba(127,127,127,.16); font-weight: 600; }
    .afs-chat-tab--closed .afs-chat-tab__label { text-decoration: line-through; opacity: .65; }
    .afs-chat-tab__label { overflow: hidden; text-overflow: ellipsis; }
    .afs-chat-tab__waiting { opacity: .7; font-size: .7rem; }
    .afs-chat-tab__badge {
      min-width: 17px; padding: 0 4px; border-radius: 9px; background: #da272a; color: #fff;
      font-size: .65rem; line-height: 17px; text-align: center;
    }
    .afs-chat-watch { padding: .25rem .6rem; font-size: .75rem; }
    .afs-chat-watch__toggle, .afs-chat-watch__row {
      display: block; width: 100%; border: 0; background: transparent; padding: .2rem 0;
      text-align: left; cursor: pointer; opacity: .75;
    }
    /* Touch target: the field team drives this on an Android tablet. */
    @media (pointer: coarse) {
      .afs-chat-tab, .afs-chat-watch__toggle, .afs-chat-watch__row { min-height: 36px; }
    }
  `],
  template: `
    @if (conversations().length > 0) {
      <div class="afs-chat-tabs">
        @for (c of conversations(); track c.id) {
          <button
            class="afs-chat-tab"
            type="button"
            [class.afs-chat-tab--active]="c.id === activeId()"
            [class.afs-chat-tab--closed]="c.status === 'closed'"
            [class.afs-chat-tab--queued]="c.status === 'queued'"
            (click)="select.emit(c.id)"
          >
            <span class="afs-chat-tab__label">{{ c.subject ?? 'Chat' }}</span>
            @if (c.status === 'queued') {
              <span class="afs-chat-tab__waiting">waiting</span>
            }
            @if ((unread()[c.id] ?? 0) > 0) {
              <span class="afs-chat-tab__badge">{{ unread()[c.id] }}</span>
            }
          </button>
        }
      </div>
    }

    @if (watching().length > 0) {
      <div class="afs-chat-watch">
        <button
          class="afs-chat-watch__toggle"
          type="button"
          [attr.aria-expanded]="open()"
          (click)="open.set(!open())"
        >Watching ({{ watching().length }})</button>

        <!-- No badge and no count here, deliberately. Spec section 9: handing a
             chat over has to actually stop it following you around. -->
        @if (open()) {
          @for (c of watching(); track c.id) {
            <button class="afs-chat-watch__row" type="button" (click)="select.emit(c.id)"
            >{{ c.subject ?? 'Chat' }}</button>
          }
        }
      </div>
    }
  `,
})
export class ChatTabsComponent {
  readonly conversations = input.required<readonly Conversation[]>();
  readonly watching = input.required<readonly Conversation[]>();
  readonly activeId = input.required<string | null>();
  /** Unread counts by conversation id. Declared with `| undefined` because a
   *  conversation nobody has read yet is simply absent from the map, and
   *  `Record<string, number>` would type that hole as a number. */
  readonly unread = input.required<Record<string, number | undefined>>();

  readonly select = output<string>();

  readonly open = signal(false);
}
